import React, { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import Modal from "metabux/components/Modal/Modal"
import { Box, Typography, CircularProgress, Alert, Checkbox, FormControlLabel } from "@mui/material"
import { Button } from "metabux/components/Button/Button"
import { useAuth0 } from '@auth0/auth0-react'
import urljoin from "url-join"
import { API_BASEPATH_PORTAL } from "@/config/env.config"
import { Document, Page, pdfjs } from "react-pdf"

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

export const EulaConfirmationModal = ({
	open,
	onAccept,
	onDecline,
	...props
}) => {
	const { getAccessTokenSilently } = useAuth0();
	const [retryCount, setRetryCount] = useState(0);
	const [fopDownloading, setFopDownloading] = useState(false);
	const [eulaLoading, setEulaLoading] = useState(false);
	const [eulaError, setEulaError] = useState(null);
	const [eulaPdfUrl, setEulaPdfUrl] = useState("");
	const [hasAcknowledgedReview, setHasAcknowledgedReview] = useState(false);
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
	const [numPages, setNumPages] = useState(0);
	const [pdfWidth, setPdfWidth] = useState(0);
	const eulaScrollRef = useRef(null);

	const checkIfScrolledToBottom = useCallback(() => {
		if (!eulaScrollRef.current) return;

		const { scrollTop, scrollHeight, clientHeight } = eulaScrollRef.current;
		const remaining = scrollHeight - clientHeight - scrollTop;
		const isAtBottom = remaining <= 4;
		const contentDoesNotOverflow = scrollHeight <= clientHeight + 1;
		setHasScrolledToBottom(isAtBottom || contentDoesNotOverflow);
	}, []);

	const loadEulaPdf = useCallback(async () => {
		try {
			setEulaLoading(true);
			setEulaError(null);
			const url = urljoin(API_BASEPATH_PORTAL, 'eula', 'download');
			const token = await getAccessTokenSilently({
				audience: 'https://portal-api'
			});

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${token}`,
					'X-Content-Type-Options': 'nosniff',
					'X-XSS-Protection': 1,
				},
			});

			if (!response.ok) {
				let message = `Failed to load EULA: ${response.statusText}`;
				try {
					const error = await response.json();
					message = error?.error || message;
				} catch {
					// Ignore JSON parse failure and keep fallback message.
				}
				throw new Error(message);
			}

			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			setEulaPdfUrl(prevUrl => {
				if (prevUrl) {
					window.URL.revokeObjectURL(prevUrl);
				}
				return blobUrl;
			});
		} catch (error) {
			console.error('Failed to load EULA PDF:', error);
			setEulaError(error?.message || 'Failed to load EULA PDF.');
		} finally {
			setEulaLoading(false);
		}
	}, [getAccessTokenSilently]);

	const handleRetry = useCallback(() => {
		setRetryCount(prev => prev + 1);
		loadEulaPdf();
	}, [loadEulaPdf]);

	const handleDownloadFop = useCallback(async () => {
		try {
			setFopDownloading(true);
			const url = urljoin(API_BASEPATH_PORTAL, 'shared-files', 'FairUsePolicy', 'download');
			const token = await getAccessTokenSilently({
				audience: 'https://portal-api'
			});
			
			// Fetch the file
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Authorization': 'Bearer' + ' ' + token,
					'X-Content-Type-Options': 'nosniff',
					'X-XSS-Protection': 1,
				},
			});
			
			if (response && !response.ok) {
				const error = await response.json();
				throw new Error(error.message || `Failed to download FOP: ${response.statusText}`);
			}
			
			// Extract filename from Content-Disposition header (now exposed by CORS)
			let filename = 'Fair-Use-Policy'; // Default fallback
			const contentDisposition = response.headers.get('Content-Disposition');
			if (contentDisposition) {
				// Parse filename from Content-Disposition: attachment; filename="filename.ext"
				const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, '');
				}
			}
			
			// Create blob and trigger download with the filename
			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(blobUrl);
		} catch (error) {
			console.error('Failed to download FOP:', error);
		} finally {
			setFopDownloading(false);
		}
	}, [getAccessTokenSilently]);

	const handleAccept = useCallback(() => {
		if (onAccept) onAccept();
	}, [onAccept]);

	const handleDecline = useCallback(() => {
		if (onDecline) onDecline();
	}, [onDecline]);

	useEffect(() => {
		if (!open) {
			setHasAcknowledgedReview(false);
			setHasScrolledToBottom(false);
			setNumPages(0);
			return;
		}

		setHasAcknowledgedReview(false);
		setHasScrolledToBottom(false);
		setNumPages(0);
		loadEulaPdf();
	}, [open, loadEulaPdf]);

	useEffect(() => {
		if (eulaLoading || eulaError || !eulaPdfUrl || !numPages) {
			setHasScrolledToBottom(false);
			return;
		}

		const frameId = window.requestAnimationFrame(checkIfScrolledToBottom);
		return () => window.cancelAnimationFrame(frameId);
	}, [eulaLoading, eulaError, eulaPdfUrl, numPages, checkIfScrolledToBottom]);

	useEffect(() => {
		if (!eulaScrollRef.current) {
			return;
		}

		const updatePdfWidth = () => {
			const measuredWidth = eulaScrollRef.current?.clientWidth || 0;
			setPdfWidth(Math.max(280, measuredWidth - 16));
		};

		updatePdfWidth();
		const resizeObserver = new ResizeObserver(updatePdfWidth);
		resizeObserver.observe(eulaScrollRef.current);
		return () => resizeObserver.disconnect();
	}, []);

	useEffect(() => {
		return () => {
			if (eulaPdfUrl) {
				window.URL.revokeObjectURL(eulaPdfUrl);
			}
		};
	}, [eulaPdfUrl]);

	// Prevent modal from being closed - user must accept or decline
	const handleModalClose = useCallback(() => {
		// No-op: Prevent closing the modal - user must click Accept or Decline
	}, []);

	return (
		<Modal
			open={open}
			onClose={handleModalClose} // Prevent closing by clicking outside or X button
			title="End User License Agreement"
			hideCloseButton={true}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 3,
					mt: 2,
					maxHeight: "70vh",
					overflowY: "auto"
				}}
			>
				<Typography variant="body1" sx={{ fontWeight: 600 }}>
					Please review and accept the End User License Agreement (PDF) to continue.
				</Typography>

				<Box
					ref={eulaScrollRef}
					onScroll={checkIfScrolledToBottom}
					sx={{
						border: "none",
						borderRadius: 0,
						p: 0,
						backgroundColor: "transparent",
						maxHeight: "400px",
						overflowY: "auto",
						overflowX: "hidden"
					}}
				>
					{eulaLoading && (
						<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
							<CircularProgress size={24} />
						</Box>
					)}
					{eulaError && (
						<Alert 
							severity="error" 
							sx={{ mb: 2 }}
							action={
								retryCount < 3 && (
									<Button 
										variant="secondary" 
										size="small" 
										onClick={handleRetry}
										sx={{ ml: 2 }}
									>
										Retry
									</Button>
								)
							}
						>
							Failed to load EULA PDF. {retryCount < 3 ? 'Please try again.' : 'Please contact support if this issue persists.'}
						</Alert>
					)}
					{!eulaLoading && !eulaError && eulaPdfUrl && (
						<Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
							<Document
								file={eulaPdfUrl}
								onLoadSuccess={({ numPages: loadedPages }) => setNumPages(loadedPages)}
								onLoadError={() => setEulaError('Failed to render EULA PDF.')}
								loading={
									<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
										<CircularProgress size={24} />
									</Box>
								}
							>
								{Array.from(new Array(numPages), (_, index) => (
									<Page
										key={`eula-page-${index + 1}`}
										pageNumber={index + 1}
										width={pdfWidth || undefined}
										renderAnnotationLayer={false}
										renderTextLayer={false}
									/>
								))}
							</Document>
						</Box>
					)}
				</Box>
				<Typography variant="body2" color="text.secondary">
					Scroll to the end of the document to enable acknowledgment.
				</Typography>
				<FormControlLabel
					control={
						<Checkbox
							checked={hasAcknowledgedReview}
							onChange={(event) => setHasAcknowledgedReview(event.target.checked)}
							disabled={eulaLoading || !!eulaError || !eulaPdfUrl || !hasScrolledToBottom}
						/>
					}
					label="I have reviewed the End User License Agreement."
				/>

				<Box sx={{ display: "flex", gap: "10px", justifyContent: "space-between", mt: 2, alignItems: "center" }}>
					<Button 
						variant="secondary" 
						size="small"
						onClick={handleDownloadFop}
						disabled={fopDownloading}
						sx={{ minWidth: "auto" }}
					>
						{fopDownloading ? 'Downloading...' : 'Download Fair Use Policy'}
					</Button>
					<Box sx={{ display: "flex", gap: "10px" }}>
						<Button 
							variant="secondary" 
							color="black" 
							onClick={handleDecline}
							sx={{ minWidth: "120px" }}
						>
							I Decline
						</Button>
						<Button 
							variant="primary" 
							onClick={handleAccept}
							disabled={!hasAcknowledgedReview}
							sx={{ minWidth: "120px" }}
						>
							I Accept
						</Button>
					</Box>
				</Box>
			</Box>
		</Modal>
	)
}

EulaConfirmationModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onAccept: PropTypes.func,
	onDecline: PropTypes.func,
}

export default EulaConfirmationModal

